import { Facebook, Instagram, Mail, MessageCircle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { ASSETS, LINKS } from "../lib/constants";

export default function Footer() {
    const year = new Date().getFullYear();
    const { pathname } = useLocation();
    const isHome = pathname === "/";
    const homeHref = (hash) => (isHome ? hash : `/${hash}`);
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-black border-t border-white/5 pt-16 pb-10"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10 lg:gap-12">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <img
                                src={ASSETS.icon}
                                alt="TRT Plus"
                                className="w-9 h-9 rounded-lg"
                            />
                            <span className="font-brand text-2xl leading-none">
                                <span className="text-white">TRT</span>
                                <span className="text-[#2ba6e0]">+</span>
                            </span>
                        </div>
                        <p className="text-white/55 text-sm max-w-sm leading-relaxed">
                            Clinical precision for TRT & enhanced athletes.
                        </p>
                        <div className="flex items-center gap-2 mt-6">
                            {[
                                {
                                    label: "Facebook",
                                    href: LINKS.facebook,
                                    icon: Facebook,
                                    tid: "social-facebook",
                                },
                                {
                                    label: "Messenger",
                                    href: LINKS.messenger,
                                    icon: MessageCircle,
                                    tid: "social-messenger",
                                },
                                {
                                    label: "Instagram",
                                    href: LINKS.instagram,
                                    icon: Instagram,
                                    tid: "social-instagram",
                                },
                                {
                                    label: "Email",
                                    href: LINKS.email,
                                    icon: Mail,
                                    tid: "social-email",
                                },
                            ].map((s) => (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target={
                                        s.href.startsWith("mailto:")
                                            ? undefined
                                            : "_blank"
                                    }
                                    rel="noreferrer"
                                    aria-label={s.label}
                                    data-testid={s.tid}
                                    className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:border-[#2ba6e0] hover:bg-[#2ba6e0]/10 transition-all"
                                >
                                    <s.icon size={16} />
                                </a>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="text-[11px] uppercase tracking-widest font-mono text-white/50 mb-4">
                            Explore
                        </div>
                        <ul className="space-y-3 text-sm">
                            {[
                                { label: "The Framework", href: "#framework" },
                                { label: "My Protocol", href: "#protocol" },
                                { label: "My Bloodwork", href: "#bloodwork" },
                                { label: "The Testimator", href: "#testimator" },
                                { label: "Engine Schematic", href: "/engine#schematic" },
                                { label: "Under the Hood", href: "/engine#formulas" },
                                {
                                    label: "Knowledge Base",
                                    href: "#knowledge",
                                },
                                { label: "Reviews", href: "#reviews" },
                                { label: "The Origin", href: "#origin" },
                                { label: "FAQ", href: "/faq" },
                                { label: "Blog", href: "/blog" },
                            ].map((l) => {
                                const isRoute = l.href.startsWith("/");
                                return (
                                    <li key={l.href}>
                                        {isRoute ? (
                                            <Link
                                                to={l.href}
                                                className="text-white/65 hover:text-white transition-colors"
                                            >
                                                {l.label}
                                            </Link>
                                        ) : (
                                            <a
                                                href={homeHref(l.href)}
                                                className="text-white/65 hover:text-white transition-colors"
                                            >
                                                {l.label}
                                            </a>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    <div>
                        <div className="text-[11px] uppercase tracking-widest font-mono text-white/50 mb-4">
                            Legal
                        </div>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <Link
                                    to="/privacy-policy"
                                    data-testid="footer-link-privacy"
                                    className="text-white/65 hover:text-white transition-colors"
                                >
                                    Privacy Policy
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/terms-of-service"
                                    data-testid="footer-link-terms"
                                    className="text-white/65 hover:text-white transition-colors"
                                >
                                    Terms of Service
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/data-deletion"
                                    data-testid="footer-link-data-deletion"
                                    className="text-white/65 hover:text-white transition-colors"
                                >
                                    Data Deletion
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <div className="text-[11px] uppercase tracking-widest font-mono text-white/50 mb-4">
                            Download
                        </div>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <a
                                    href={LINKS.appStore}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-white/65 hover:text-white transition-colors"
                                >
                                    App Store
                                </a>
                            </li>
                            <li>
                                <a
                                    href={LINKS.googlePlay}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-white/65 hover:text-white transition-colors"
                                >
                                    Google Play
                                </a>
                            </li>
                            <li>
                                <a
                                    href={LINKS.email}
                                    className="text-white/65 hover:text-white transition-colors"
                                >
                                    support@trtplus.app
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="hairline my-10" />

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/40">
                    <div>
                        © {year} TRT Plus. All rights reserved.
                    </div>
                    <div className="font-mono">
                        Built for TRT & enhanced athletes.
                    </div>
                </div>
            </div>
        </footer>
    );
}
